import React, { JSX, useState } from 'react';
import FeeTypesSetup from './FeeTypesSetup';
import FeeStructureSetup from './FeeStructureSetup';
import FeeLateConfigSetup from './FeeLateConfigSetup';

// ── Inline SVG icons ──────────────────────────────────────────────────────────
const Ico = ({ d, size = 16, style = {}, fill = false }: { d: string | string[]; size?: number; style?: React.CSSProperties; fill?: boolean }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24"
    fill={fill ? 'currentColor' : 'none'}
    stroke={fill ? 'none' : 'currentColor'}
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', ...style }}
  >
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const IcoWallet = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M21 12V7H5a2 2 0 010-4h14v4M21 12v5H5a2 2 0 000 4h16v-5M21 12H9" />;

const IcoInfo = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zM12 8v4M12 16h.01" />;

const IcoList = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />;

const IcoCalendar = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d={['M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z']} />;

const IcoClock = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zM12 6v6l4 2" />;

// ── Tab config ────────────────────────────────────────────────────────────────
type Tab = 'types' | 'structure' | 'late';

const TABS: {
  id: Tab;
  label: string;
  Icon: (p: { size?: number; style?: React.CSSProperties }) => JSX.Element;
  desc: string;
}[] = [
  {
    id: 'types',
    label: 'Fee Types',
    Icon: IcoList,
    desc: 'Define fee categories (Tuition, Transport, etc.)',
  },
  {
    id: 'structure',
    label: 'Fee Structure',
    Icon: IcoCalendar,
    desc: 'Assign monthly amounts per class & session',
  },
  {
    id: 'late',
    label: 'Late Fee Config',
    Icon: IcoClock,
    desc: 'Due dates, grace days & late fee rules',
  },
];

// ── Styles ────────────────────────────────────────────────────────────────────
const GS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
  .fee-shell * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }

  @keyframes fee-tabFade { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  .fee-tab-content { animation: fee-tabFade 0.25s ease both; }

  .fee-tab-btn {
    display: flex; flex-direction: column; align-items: flex-start;
    padding: 14px 20px; border-radius: 14px; cursor: pointer;
    border: 2px solid transparent; background: rgba(255,255,255,0.08);
    transition: all 0.18s; flex: 1; min-width: 160px;
    font-family: 'DM Sans', sans-serif;
    outline: none;
  }
  .fee-tab-btn:hover { background: rgba(255,255,255,0.15); border-color: rgba(255,255,255,0.25); }
  .fee-tab-btn.active {
    background: white; border-color: white;
    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  }
  .fee-tab-label {
    font-size: 14px; font-weight: 700;
    color: rgba(255,255,255,0.65);
    display: flex; align-items: center; gap: 8px; transition: color 0.15s;
  }
  .fee-tab-btn.active .fee-tab-label { color: #1e3a8a; }
  .fee-tab-desc {
    font-size: 11px; color: rgba(255,255,255,0.45); margin-top: 4px;
    font-weight: 500; transition: color 0.15s; padding-left: 30px;
  }
  .fee-tab-btn.active .fee-tab-desc { color: #64748b; }

  .fee-step-dot {
    width: 22px; height: 22px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 800; flex-shrink: 0;
    background: rgba(255,255,255,0.18); color: rgba(255,255,255,0.7);
    transition: all 0.15s;
  }
  .fee-tab-btn.active .fee-step-dot { background: #1e3a8a; color: white; }

  .fee-active-badge {
    margin-left: 4px; font-size: 10px; font-weight: 700;
    background: #dbeafe; color: #1e40af;
    padding: 1px 7px; border-radius: 99px;
  }
  .fee-tab-btn.active .fee-active-badge-inactive { display: none; }
`;

// ── Component ─────────────────────────────────────────────────────────────────
export default function FeeSetup() {
  const [activeTab, setActiveTab] = useState<Tab>('types');

  return (
    <div className="fee-shell" style={{ minHeight: '100vh', background: '#f6f8fc', padding: 0 }}>
      <style>{GS}</style>

      {/* ── Header ── */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #2855ae 50%, #3b82f6 100%)',
        borderRadius: 24,
        padding: '28px 32px',
        marginBottom: 24,
        boxShadow: '0 8px 32px rgba(30,58,138,0.22)',
      }}>

        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <div style={{
            background: 'rgba(255,255,255,0.18)',
            borderRadius: 16,
            width: 52, height: 52,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <IcoWallet size={22} style={{ color: 'white' }} />
          </div>

          <div>
            <h1 style={{ color: 'white', fontSize: 24, fontWeight: 800, margin: '0 0 4px', letterSpacing: '-0.3px' }}>
              Fee Management
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, margin: 0, fontWeight: 500 }}>
              Set up fee types, structures, and late fee rules per class
            </p>
          </div>

          {/* Hint chip */}
          <div style={{
            marginLeft: 'auto',
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 10,
            padding: '8px 14px',
          }}>
            <IcoInfo size={13} style={{ color: 'rgba(255,255,255,0.65)' }} />
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
              Complete steps in order for best results
            </span>
          </div>
        </div>

        {/* Tab buttons */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {TABS.map((tab, i) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                className={`fee-tab-btn ${isActive ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <div className="fee-tab-label">
                  <span className="fee-step-dot">{i + 1}</span>
                  <tab.Icon
                    size={13}
                    style={{ color: isActive ? '#2855ae' : 'rgba(255,255,255,0.7)' }}
                  />
                  {tab.label}
                  {isActive && <span className="fee-active-badge">Active</span>}
                </div>
                <div className="fee-tab-desc">{tab.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Tab content ── */}
      <div className="fee-tab-content" key={activeTab}>
        {activeTab === 'types'     && <FeeTypesSetup />}
        {activeTab === 'structure' && <FeeStructureSetup />}
        {activeTab === 'late'      && <FeeLateConfigSetup />}
      </div>
    </div>
  );
}